import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Download, ArrowLeft, Trophy } from "lucide-react";
import api, { API_BASE } from "@/lib/api";

export default function ResultDetail() {
  const { id } = useParams();
  const [r, setR] = useState(null);
  useEffect(() => { api.get(`/results/${id}`).then(res => setR(res.data)); }, [id]);
  if (!r) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  const passed = r.status === "passed";
  const Icon = passed ? CheckCircle2 : XCircle;
  const color = passed ? "text-emerald-600" : "text-red-600";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-2xl mx-auto px-6 py-12">
        <Link to="/student" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>
        <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
          <Icon className={`w-16 h-16 ${color} mx-auto`} strokeWidth={1.6} />
          <h1 className={`mt-4 text-4xl tracking-tight font-semibold ${color}`}>
            {passed ? "Congratulations!" : "Better luck next time"}
          </h1>
          <p className="mt-2 text-slate-500">{r.assessment_title}</p>

          <div className={`mt-8 inline-block text-6xl tracking-tight font-semibold ${color}`} data-testid="result-percentage">
            {r.percentage}%
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <Stat label="Score" value={`${r.score}/${r.total}`} />
            <Stat label="Time" value={`${Math.floor((r.time_taken||0)/60)}m ${(r.time_taken||0)%60}s`} />
            <Stat label="Status" value={r.status.toUpperCase()} />
          </div>

          {passed && (
            <a
              data-testid="download-certificate-button"
              href={`${API_BASE}/results/${r.id}/certificate`}
              className="mt-8 inline-flex items-center gap-2 bg-blue-600 text-white rounded-lg px-5 py-2.5 font-medium hover:bg-blue-700">
              <Trophy className="w-4 h-4" /> <Download className="w-4 h-4" /> Download certificate
            </a>
          )}

          {(r.violation_count > 0 || r.auto_submitted) && (
            <div data-testid="result-integrity-notice"
              className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-left">
              <div className="font-medium text-amber-900">
                Integrity notice — {r.violation_count} warning{r.violation_count === 1 ? "" : "s"} recorded
                {r.auto_submitted ? " · auto-submitted at threshold" : ""}
              </div>
              <p className="text-xs text-amber-800/90 mt-1">
                The trainer reviewing this attempt will see the list of warnings (tab-switch, fullscreen exit, copy/paste, etc.).
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
